import React, { PropsWithChildren } from 'react';
import { noop } from '@highlight-ui/utils-commons';

import Option, { OptionProps } from './components/Option';
import OptionGroup from './components/OptionGroup';
import SelectAllOption from './components/SelectAllOption';
import { SelectOption, SelectOptionType } from '../types';
import { applyProps } from '../helpers';

export type ListBoxProps<T> = PropsWithChildren<{
  /**
   * ⚠️ _Consult a designer before using this prop, in most cases it shouldn't be used._
   * Determines whether or not a search input should be rendered in the select list. If set to true, the search input will only render if there are more than 7 options.
   */
  searchable?: boolean;
  /** Set to `true` when you have a `multi-select` scenario */
  multiple?: boolean;
  /** Highlights this text in the options list only when the Select is not searchable. */
  highlightedText?: string;
  searchInputValue: string;
  selectAllIsSelected: boolean;
  selectedOptionsWithValuesOnly: (string | number)[];
  listRef: React.Ref<HTMLUListElement>;
  onKeyDown?: (e: React.KeyboardEvent<HTMLLIElement>) => void;
  handleSelect: (option: T) => void;
  handleSelectAll: (selected: boolean) => void;
  optionType?: SelectOptionType;
}>;

export type ListBoxOptionProps<T extends SelectOption> = Omit<
  OptionProps<T>,
  'highlightedText' | 'onSelect' | 'selected' | 'optionStyle' | 'optionType'
>;
type ListboxOption<T extends SelectOption> = React.FC<
  React.PropsWithChildren<ListBoxOptionProps<T>>
>;

const ListBox = <T extends SelectOption>({
  children,
  searchable = true,
  highlightedText = '',
  multiple = false,
  searchInputValue = '',
  selectAllIsSelected,
  selectedOptionsWithValuesOnly,
  listRef,
  onKeyDown = noop,
  handleSelect,
  handleSelectAll,
  optionType = 'single-line',
}: ListBoxProps<T>) => {
  const subcomponents = applyProps(children, (child) => {
    const isOption =
      (child as JSX.Element).type.displayName === Option.displayName;
    const isSelectAll = child.type === SelectAllOption;
    const isOptionGroup = child.type === OptionGroup;

    if (isOption) {
      const { value } = child.props as ListBoxOptionProps<T> & {
        value: SelectOption['value'];
      };

      return React.cloneElement<OptionProps<T>>(child as React.ReactElement, {
        onSelect: handleSelect,
        onKeyDown,
        selected: selectedOptionsWithValuesOnly.includes(value),
        optionStyle: multiple ? 'checkbox' : 'checkmark',
        optionType: optionType ?? 'single-line',
        highlightedText: searchable ? searchInputValue : highlightedText,
      });
    }

    if (isSelectAll) {
      if (searchInputValue?.length === 0 && multiple) {
        return React.cloneElement(child as React.ReactElement, {
          onSelect: handleSelectAll,
          onKeyDown,
          isSelected: selectAllIsSelected,
        });
      }
      return null;
    }

    if (isOptionGroup) {
      return child;
    }

    return null;
  });

  return (
    <ul role="listbox" ref={listRef}>
      {subcomponents}
    </ul>
  );
};

ListBox as <T extends SelectOption>(
  props: ListBoxProps<T> & {
    SelectAllOption: typeof SelectAllOption;
    OptionGroup: typeof OptionGroup;
    Option: ListboxOption<T>;
  },
) => ReturnType<typeof ListBox>;

ListBox.displayName = 'ListBox';

ListBox.SelectAllOption = SelectAllOption;
ListBox.OptionGroup = OptionGroup;
ListBox.Option = Option as ListboxOption<any>;
export default ListBox;
